import { useState, useEffect, useCallback } from 'react';
import { Button, Spin, Modal } from 'antd';
import {
  BuildOutlined, CalendarOutlined, DownloadOutlined, EyeOutlined, FilePdfOutlined, FileTextOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang } from '../shared/utils';
import { CommunityTab } from './Community';
import { ContributionsTab } from './Contributions';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export function ActivityTab() {
  const [items,      setItems]      = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [catFilter,  setCatFilter]  = useState('');
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);
  const [expanded,   setExpanded]   = useState({});
  const PAGE_SIZE = 10;

  // Load categories once
  useEffect(() => {
    axios.get(`${baseUrl}/member/portal-categories`, { headers: authHeader() })
      .then(r => { if (r.data.status) setCategories(r.data.result || []); })
      .catch(() => {});
  }, []);

  const NEWS_PATTERN = /समाचार|घोषणा|सूचना/;
  const activityCats = categories.filter(c => !NEWS_PATTERN.test(c.name));

  const fetchUpdates = useCallback(async (pg = 1, cat = catFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE, exclude_news: '1' });
      if (cat) params.set('category_id', cat);
      const res = await axios.get(`${baseUrl}/member/portal-updates?${params}`, { headers: authHeader() });
      if (res.data.status) {
        setItems(res.data.result || []);
        setTotal(res.data.total || 0);
        setPage(pg);
      }
    } catch {}
    finally { setLoading(false); }
  }, [catFilter]);

  useEffect(() => { fetchUpdates(1, catFilter); }, [catFilter]); // eslint-disable-line

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return d; }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

  return (
    <div>
      {/* Category filter chips */}
      {activityCats.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={() => setCatFilter('')}
            style={{
              fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 6,
              cursor: 'pointer', border: `1px solid ${!catFilter ? M : '#e5e7eb'}`,
              background: !catFilter ? M : '#fff', color: !catFilter ? G : '#374151',
            }}>
            सभी
          </button>
          {activityCats.map(c => (
            <button key={c.id} onClick={() => setCatFilter(String(c.id))}
              style={{
                fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 6,
                cursor: 'pointer',
                border: `1px solid ${catFilter === String(c.id) ? c.color : '#e5e7eb'}`,
                background: catFilter === String(c.id) ? c.color : '#fff',
                color: catFilter === String(c.id) ? '#fff' : c.color,
              }}>
              {c.name}
            </button>
          ))}
        </div>
      )}

      {!items.length ? (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 28, textAlign: 'center' }}>
          <BuildOutlined style={{ fontSize: 36, color: '#9ca3af', marginBottom: 10 }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>कोई गतिविधि नहीं</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Admin जल्द ही अपडेट जोड़ेगा।</div>
        </div>
      ) : items.map(item => {
        const catColor = item.category_color || '#6b7280';
        const isExp = expanded[item.id];
        const longContent = item.content && item.content.length > 180;
        return (
          <div key={item.id} style={{
            background: '#fff', borderRadius: 12, marginBottom: 12, overflow: 'hidden',
            border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}>
            {/* Image */}
            {item.image_url && (
              <div style={{ width: '100%', maxHeight: 240, overflow: 'hidden', background: '#f3f4f6' }}>
                <img src={item.image_url} alt={item.title}
                  style={{ width: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            )}

            <div style={{ padding: '12px 14px' }}>
              {/* Category + date */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                {item.category_name ? (
                  <span style={{
                    fontSize: 10, fontWeight: 800,
                    color: '#fff',
                    background: catColor,
                    borderRadius: 20, padding: '3px 10px',
                    letterSpacing: 0.4,
                    boxShadow: `0 2px 6px ${catColor}55`,
                  }}>
                    {item.category_name}
                  </span>
                ) : <span />}
                {item.post_date && (
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>
                    <CalendarOutlined style={{ marginRight: 3 }} />{fmtDate(item.post_date)}
                  </span>
                )}
              </div>

              {/* Title */}
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6, lineHeight: 1.4 }}>
                {item.title}
              </div>

              {/* Content */}
              {item.content && (
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                  {longContent && !isExp ? item.content.slice(0, 180) + '…' : item.content}
                  {longContent && (
                    <button onClick={() => setExpanded(e => ({ ...e, [item.id]: !isExp }))}
                      style={{ marginLeft: 6, fontSize: 11, color: M, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      {isExp ? 'कम दिखाएं' : 'और पढ़ें'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 14, alignItems: 'center' }}>
          <Button size="small" disabled={page === 1} onClick={() => fetchUpdates(page - 1)} style={{ borderRadius: 7 }}>← पिछला</Button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Page {page} / {totalPages}</span>
          <Button size="small" disabled={page >= totalPages} onClick={() => fetchUpdates(page + 1)} style={{ borderRadius: 7 }}>अगला →</Button>
        </div>
      )}
    </div>
  );
}

// ── Report content renderer — parses structured JSON or plain text ────────────
export function ReportContent({ raw }) {
  if (!raw) return null;
  let data = { text: '', points: [], expenditure: [] };
  try {
    const p = JSON.parse(raw);
    data = { text: p.text || '', points: p.points || [], expenditure: p.expenditure || [] };
  } catch {
    data.text = raw;
  }
  const totalExp = data.expenditure.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const hasContent = data.text || data.points.length || data.expenditure.length;
  if (!hasContent) return null;

  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px 14px' }}>
      {data.text && (
        <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7, marginBottom: data.points.length || data.expenditure.length ? 10 : 0 }}>
          {data.text}
        </div>
      )}
      {data.points.length > 0 && (
        <div style={{ marginBottom: data.expenditure.length ? 10 : 0 }}>
          {data.points.map((pt, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 12, color: '#374151' }}>
              <span style={{ color: M, fontWeight: 800, flexShrink: 0 }}>•</span>
              <span>{pt}</span>
            </div>
          ))}
        </div>
      )}
      {data.expenditure.length > 0 && (
        <div style={{ background: '#f8fafc', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          <div style={{ background: '#7a0000', padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#fff' }}>
            व्यय विवरण
          </div>
          {data.expenditure.map((row, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '7px 10px', borderBottom: i < data.expenditure.length - 1 ? '1px solid #f3f4f6' : 'none',
              background: i % 2 === 0 ? '#fff' : '#fafafa',
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{row.label}</div>
                {row.note && <div style={{ fontSize: 10, color: '#9ca3af' }}>{row.note}</div>}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: M, flexShrink: 0 }}>
                ₹ {Number(row.amount || 0).toLocaleString('en-IN')}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px',
            background: '#7a0000', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>कुल व्यय</span>
            <span style={{ fontSize: 14, fontWeight: 900, color: G }}>₹ {totalExp.toLocaleString('en-IN')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Single report card ────────────────────────────────────────────────────────
export function ReportCard({ r, onView, onDownload, isPdf, formatPeriod, isYearly }) {
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${isYearly ? '#bbf7d0' : '#e5e7eb'}`,
      borderRadius: 12, marginBottom: 10, overflow: 'hidden',
    }}>
      <div style={{
        background: isYearly ? '#f0fdf4' : '#f8fafc',
        borderBottom: '1px solid #f3f4f6', padding: '10px 14px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isPdf(r.file_url)
            ? <FilePdfOutlined style={{ color: '#ef4444', fontSize: 16 }} />
            : <FileTextOutlined style={{ color: M, fontSize: 16 }} />
          }
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{r.title}</span>
              {isYearly && (
                <span style={{ fontSize: 9, fontWeight: 800, background: '#16a34a', color: '#fff',
                  borderRadius: 20, padding: '1px 7px', textTransform: 'uppercase' }}>वार्षिक</span>
              )}
            </div>
            {r.report_month && (
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                <CalendarOutlined style={{ marginRight: 4 }} />{formatPeriod(r.report_month)}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {r.file_url && (
            <button onClick={() => onView(r)}
              style={{ fontSize: 11, color: M, background: '#fff5f5', border: `1px solid ${M}`,
                borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 4 }}>
              <EyeOutlined /> देखें
            </button>
          )}
          {r.file_url && (
            <button onClick={() => onDownload(r.file_url, r.file_name || r.title)}
              style={{ fontSize: 11, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe',
                borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 4 }}>
              <DownloadOutlined /> डाउनलोड
            </button>
          )}
        </div>
      </div>
      <ReportContent raw={r.description} />
    </div>
  );
}

// ── Reports sub-tab ───────────────────────────────────────────────────────────
export function ReportsSubTab() {
  const [reports,    setReports]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [yearFilter, setYearFilter] = useState('');
  const [preview,    setPreview]    = useState(null);

  useEffect(() => {
    axios.get(`${baseUrl}/member/reports`, { headers: authHeader() })
      .then(r => { if (r.data.status) setReports(r.data.result || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Detect period type from report_month value
  const getPeriodType = (rm) => {
    if (!rm) return 'monthly';
    if (/^\d{4}$/.test(rm)) return 'yearly';
    if (!/^\d{4}-\d{2}$/.test(rm)) return 'custom';
    return 'monthly';
  };
  const formatPeriod = (rm) => {
    if (!rm) return '—';
    if (/^\d{4}$/.test(rm)) return rm;
    if (/^\d{4}-\d{2}$/.test(rm)) {
      try { return new Date(rm + '-01').toLocaleDateString('hi-IN', { month: 'long', year: 'numeric' }); }
      catch { return rm; }
    }
    return rm;
  };

  // Extract unique years for filter
  const years = [...new Set(reports.map(r => {
    const y = (r.report_month || '').match(/\d{4}/);
    return y ? y[0] : null;
  }).filter(Boolean))].sort((a, b) => b - a);

  const filtered = yearFilter
    ? reports.filter(r => (r.report_month || '').includes(yearFilter))
    : reports;

  // Split into yearly and monthly/custom
  const yearlyReports  = filtered.filter(r => getPeriodType(r.report_month) === 'yearly');
  const periodicReports = filtered.filter(r => getPeriodType(r.report_month) !== 'yearly');

  const isPdf = (url) => url && (url.toLowerCase().endsWith('.pdf') || url.includes('.pdf'));

  const openPreview = (report) => {
    setPreview({ title: report.title, url: report.file_url, isPdfFile: isPdf(report.file_url) });
  };

  const download = (url, name) => {
    const a = document.createElement('a');
    a.href = url; a.download = name || 'report'; a.target = '_blank'; a.click();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BuildOutlined style={{ fontSize: 20, color: G }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: G }}>संस्था विकास कार्य</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>मासिक रिपोर्ट · व्यय विवरण · नोट्स</div>
          </div>
        </div>
      </div>

      {/* Year filter */}
      {years.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={() => setYearFilter('')}
            style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
              background: !yearFilter ? M : '#f3f4f6', color: !yearFilter ? G : '#374151', border: 'none' }}>
            सभी वर्ष
          </button>
          {years.map(y => (
            <button key={y} onClick={() => setYearFilter(y)}
              style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
                background: yearFilter === y ? M : '#f3f4f6', color: yearFilter === y ? G : '#374151', border: 'none' }}>
              {y}
            </button>
          ))}
        </div>
      )}

      {!filtered.length ? (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 28, textAlign: 'center' }}>
          <BuildOutlined style={{ fontSize: 36, color: '#9ca3af', marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>कोई रिपोर्ट उपलब्ध नहीं</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Admin द्वारा रिपोर्ट प्रकाशित होने पर यहाँ दिखेगी।</div>
        </div>
      ) : (
        <>
          {/* ── Yearly reports ── */}
          {yearlyReports.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                <CalendarOutlined /> वार्षिक रिपोर्ट ({yearlyReports.length})
              </div>
              {yearlyReports.map(r => <ReportCard key={r.id} r={r} onView={openPreview} onDownload={download} isPdf={isPdf} formatPeriod={formatPeriod} isYearly />)}
              {periodicReports.length > 0 && <div style={{ borderTop: '1.5px dashed #e5e7eb', margin: '14px 0' }} />}
            </>
          )}

          {/* ── Monthly / custom reports ── */}
          {periodicReports.length > 0 && (
            <>
              {yearlyReports.length > 0 && (
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CalendarOutlined /> मासिक / अन्य रिपोर्ट ({periodicReports.length})
                </div>
              )}
              {periodicReports.map(r => <ReportCard key={r.id} r={r} onView={openPreview} onDownload={download} isPdf={isPdf} formatPeriod={formatPeriod} />)}
            </>
          )}
        </>
      )}

      {/* Preview Modal */}
      <Modal open={!!preview} onCancel={() => setPreview(null)} footer={null} centered width={680}
        title={<span style={{ color: M, fontWeight: 700 }}><FilePdfOutlined style={{ marginRight: 8 }} />{preview?.title}</span>}>
        {preview && (
          <div>
            {preview.isPdfFile ? (
              <iframe src={preview.url} title={preview.title}
                style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }} />
            ) : (
              <img src={preview.url} alt={preview.title}
                style={{ width: '100%', borderRadius: 8, display: 'block' }} />
            )}
            <Button block icon={<DownloadOutlined />}
              onClick={() => download(preview.url, preview.title)}
              style={{ marginTop: 12, height: 42, borderRadius: 8, background: M, borderColor: M, color: G, fontWeight: 700 }}>
              डाउनलोड करें
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Development Tab — wrapper with sub-tabs ───────────────────────────────────
export function DevelopmentTab({ initialSubTab }) {
  const [subTab,     setSubTab]     = useState(initialSubTab || 'activity');
  const [expData,    setExpData]    = useState(null);
  const [expLoading, setExpLoading] = useState(false);
  const [selectedFY, setSelectedFY] = useState('');
  const [expPage,    setExpPage]    = useState(1);
  // Shiksha Nidhi sub-tab state
  const [shkList,    setShkList]    = useState([]);
  const [shkTotal,   setShkTotal]   = useState(0);
  const [shkPage,    setShkPage]    = useState(1);
  const [shkFY,      setShkFY]      = useState('');
  const [shkFYList,  setShkFYList]  = useState([]);
  const [shkLoading, setShkLoading] = useState(false);

  // Auto-load shiksha list if opened via deep-link
  useEffect(() => {
    if (initialSubTab === 'shiksha') loadShiksha('', 1);
  }, []); // eslint-disable-line

  const CAT_COLORS = {
    'Education':'#1d4ed8','Scholarship':'#7c3aed','Construction':'#b45309',
    'Event':'#0f766e','Medical':'#be123c','Legal':'#374151','Other':'#6b7280',
  };

  const loadExp = (fy, page) => {
    setExpLoading(true);
    const q = fy ? `&fy=${encodeURIComponent(fy)}` : '';
    axios.get(`${baseUrl}/member/expenditure-public?page=${page}${q}`, { headers: authHeader() })
      .then(r => { if (r.data.status) setExpData(r.data); })
      .catch(() => {}).finally(() => setExpLoading(false));
  };

  const loadShiksha = (fy, page) => {
    setShkLoading(true);
    const q = fy ? `&fy=${encodeURIComponent(fy)}` : '';
    // Load year list from summary API if not yet loaded
    if (!shkFYList.length) {
      axios.get(`${baseUrl}/member/scholarships`, { headers: authHeader() })
        .then(r => {
          if (r.data.status && r.data.years?.length) {
            setShkFYList(r.data.years.map(y => y.year).reverse());
          }
        }).catch(() => {});
    }
    axios.get(`${baseUrl}/member/scholarships/list?page=${page}${q}`, { headers: authHeader() })
      .then(r => {
        if (r.data.status) {
          setShkList(r.data.result || []);
          setShkTotal(r.data.total || 0);
        }
      }).catch(() => {}).finally(() => setShkLoading(false));
  };

  const subTabs = [
    { key: 'activity',    label: 'गतिविधि'    },
    { key: 'reports',     label: 'रिपोर्ट'     },
    { key: 'expenditure', label: 'व्यय विवरण' },
    { key: 'shiksha',     label: '🎓 शिक्षा निधि' },
  ];

  return (
    <div>
      {/* Sub-tab bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: '#fff', borderRadius: 10, padding: 5, border: '1px solid #e5e7eb', width: 'fit-content' }}>
        {subTabs.map(t => (
          <button key={t.key} onClick={() => { setSubTab(t.key); if (t.key === 'expenditure' && !expData) loadExp('', 1); if (t.key === 'shiksha' && !shkList.length) loadShiksha('', 1); }} style={{
            fontSize: 12, fontWeight: 700, padding: '6px 16px', borderRadius: 8, cursor: 'pointer', border: 'none',
            background: subTab === t.key ? M : 'transparent',
            color:      subTab === t.key ? G : '#6b7280',
            transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === 'activity'    && <ActivityTab />}
      {subTab === 'reports'     && <ReportsSubTab />}

      {/* ── शिक्षा निधि योजना — full paginated list ── */}
      {subTab === 'shiksha' && (
        <div>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg,#7a0000,#b45309)', borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#fde68a', textTransform: 'uppercase', letterSpacing: 0.8 }}>ABRSVS — शिक्षा निधि योजना</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#fff', marginTop: 3 }}>आर्थिक सहयोग प्राप्त बालक-बालिका सूची</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>कुल {shkTotal} विद्यार्थी · वर्षवार विवरण</div>
          </div>

          {/* Year filter */}
          {shkFYList.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
              <button onClick={() => { setShkFY(''); setShkPage(1); loadShiksha('', 1); }}
                style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', background: shkFY === '' ? '#7a0000' : '#f3f4f6', color: shkFY === '' ? '#fde68a' : '#374151' }}>
                सभी वर्ष
              </button>
              {shkFYList.map(fy => (
                <button key={fy} onClick={() => { setShkFY(fy); setShkPage(1); loadShiksha(fy, 1); }}
                  style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', background: shkFY === fy ? '#7a0000' : '#f3f4f6', color: shkFY === fy ? '#fde68a' : '#374151' }}>
                  {fy}
                </button>
              ))}
            </div>
          )}

          {/* Table */}
          {shkLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : shkList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>कोई जानकारी उपलब्ध नहीं</div>
          ) : (
            <>
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 12 }}>
                {/* Table header */}
                <div style={{ display: 'grid', gridTemplateColumns: '36px 70px 70px 1fr 90px 90px 1fr', background: '#7a0000', padding: '8px 12px', gap: 8 }}>
                  {['#', 'वर्ष', 'लिंग', 'नाम', 'गांव', 'जिला', 'पारिवारिक स्थिति'].map(h => (
                    <div key={h} style={{ fontSize: 10, fontWeight: 800, color: '#fde68a', textAlign: h === '#' ? 'center' : 'left' }}>{h}</div>
                  ))}
                </div>

                {/* Table rows */}
                {shkList.map((s, i) => {
                  const rowNum = (shkPage - 1) * 30 + i + 1;
                  const isBoy  = s.gender === 'Male';
                  return (
                    <div key={`${s.id}-${s.fiscal_year}`}
                      style={{ display: 'grid', gridTemplateColumns: '36px 70px 70px 1fr 90px 90px 1fr', padding: '9px 12px', gap: 8, borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa', alignItems: 'center' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', fontWeight: 600 }}>{rowNum}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#7a0000', background: '#fef2f2', borderRadius: 6, padding: '2px 6px', textAlign: 'center' }}>{s.fiscal_year}</div>
                      <div style={{ fontSize: 12, textAlign: 'center' }}>
                        <span style={{ background: isBoy ? '#eff6ff' : '#fdf2f8', borderRadius: 20, padding: '2px 7px', fontSize: 10, fontWeight: 700, color: isBoy ? '#1d4ed8' : '#be185d' }}>
                          {isBoy ? 'बालक' : 'बालिका'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                      <div style={{ fontSize: 11, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.village || '-'}</div>
                      <div style={{ fontSize: 11, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.district || '-'}</div>
                      <div style={{ fontSize: 10, color: s.family_status && s.family_status !== 'उल्लेख नहीं' ? '#b45309' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.family_status || '-'}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {shkTotal > 30 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>कुल {shkTotal} विद्यार्थी</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => { const p = Math.max(1, shkPage - 1); setShkPage(p); loadShiksha(shkFY, p); }}
                      disabled={shkPage === 1}
                      style={{ background: shkPage === 1 ? '#f3f4f6' : '#7a0000', color: shkPage === 1 ? '#9ca3af' : '#fde68a', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: shkPage === 1 ? 'default' : 'pointer' }}>
                      ← पिछला
                    </button>
                    <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>{shkPage} / {Math.ceil(shkTotal / 30)}</span>
                    <button onClick={() => { const p = shkPage + 1; setShkPage(p); loadShiksha(shkFY, p); }}
                      disabled={shkPage >= Math.ceil(shkTotal / 30)}
                      style={{ background: shkPage >= Math.ceil(shkTotal / 30) ? '#f3f4f6' : '#7a0000', color: shkPage >= Math.ceil(shkTotal / 30) ? '#9ca3af' : '#fde68a', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      अगला →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {subTab === 'expenditure' && (
        <div>
          {expLoading ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          ) : expData ? (
            <>
              {/* FY selector */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>व्यय विवरण</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>Approved Expenditures — कुल {expData.total}</div>
                </div>
                {expData.fy_list?.length > 0 && (
                  <select value={selectedFY}
                    onChange={e => { setSelectedFY(e.target.value); setExpPage(1); loadExp(e.target.value, 1); }}
                    style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>
                    <option value="">सभी वर्ष</option>
                    {expData.fy_list.map(f => (
                      <option key={f.fiscal_year} value={f.fiscal_year}>FY {f.fiscal_year} — ₹{Number(f.total).toLocaleString('en-IN')}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* FY summary cards */}
              {!selectedFY && expData.fy_list?.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
                  {expData.fy_list.slice(0, 4).map(f => (
                    <div key={f.fiscal_year} onClick={() => { setSelectedFY(f.fiscal_year); setExpPage(1); loadExp(f.fiscal_year, 1); }}
                      style={{ background: '#fff', borderRadius: 10, border: `1.5px solid ${M}25`, padding: '10px 12px', cursor: 'pointer', textAlign: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = M}
                      onMouseLeave={e => e.currentTarget.style.borderColor = `${M}25`}>
                      <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>FY {f.fiscal_year}</div>
                      <div style={{ fontSize: 17, fontWeight: 900, color: M, marginTop: 3 }}>₹{Number(f.total).toLocaleString('en-IN')}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>{f.count} कार्य →</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Category breakdown — collapsible via native details */}
              {expData.category_breakdown?.length > 0 && (
                <details open style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14 }}>
                  <summary style={{ padding: '12px 14px', fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
                    <span>श्रेणीवार व्यय {selectedFY ? `— FY ${selectedFY}` : ''}</span>
                    <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>छुपाएं / दिखाएं ▾</span>
                  </summary>
                  <div style={{ padding: '0 14px 14px' }}>
                    {expData.category_breakdown.map(cat => {
                      const total = expData.category_breakdown.reduce((s, c) => s + Number(c.total), 0);
                      const pct   = total > 0 ? Math.round((Number(cat.total) / total) * 100) : 0;
                      const color = CAT_COLORS[cat.category] || '#6b7280';
                      return (
                        <div key={cat.category} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{cat.category || 'अन्य'} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({cat.count} कार्य)</span></span>
                            <span style={{ fontSize: 12, fontWeight: 700, color }}>₹{Number(cat.total).toLocaleString('en-IN')} <span style={{ color:'#9ca3af', fontWeight:400 }}>({pct}%)</span></span>
                          </div>
                          <div style={{ height: 6, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              {/* Expenditure list with work details */}
              {expData.result?.length > 0 ? (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
                    कार्यवार विवरण {selectedFY ? `— FY ${selectedFY}` : ''} ({expData.total})
                  </div>
                  {[...expData.result].sort((a, b) => Number(b.amount) - Number(a.amount)).map(e => {
                    const catColor = CAT_COLORS[e.purpose_category] || '#6b7280';
                    return (
                      <div key={e.id} style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 8, overflow: 'hidden' }}>
                        {/* Category color strip + header */}
                        <div style={{ borderLeft: `4px solid ${catColor}`, padding: '12px 14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
                              <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{e.purpose_name || 'सामान्य व्यय'}</div>
                              {e.beneficiary_name && (
                                <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>
                                  <span style={{ color: '#6b7280' }}>लाभार्थी: </span>{e.beneficiary_name}
                                  {e.beneficiary_type && <span style={{ color: '#9ca3af' }}> • {e.beneficiary_type}</span>}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                                {e.purpose_category && (
                                  <span style={{ fontSize: 10, background: `${catColor}15`, color: catColor, borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>{e.purpose_category}</span>
                                )}
                                {e.txn_date && <span style={{ fontSize: 10, color: '#9ca3af' }}><CalendarOutlined style={{ marginRight: 3, fontSize: 9 }} />{new Date(e.txn_date).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                {e.payment_mode && <span style={{ fontSize: 10, color: '#9ca3af', background: '#f3f4f6', borderRadius: 20, padding: '2px 7px' }}>{e.payment_mode}</span>}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 16, fontWeight: 900, color: Number(e.amount) > 0 ? '#b91c1c' : '#9ca3af' }}>
                                {Number(e.amount) > 0 ? `₹${Number(e.amount).toLocaleString('en-IN')}` : '—'}
                              </div>
                              <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }}>FY {e.fiscal_year}</div>
                            </div>
                          </div>
                          {/* Work done / remarks */}
                          {e.remarks && (
                            <div style={{ marginTop: 8, padding: '7px 10px', background: '#f0fdf4', borderRadius: 6, borderLeft: '3px solid #16a34a', fontSize: 11, color: '#15803d', lineHeight: 1.6 }}>
                              <span style={{ fontWeight: 700 }}>कार्य विवरण: </span>{e.remarks}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {expData.total > expData.limit && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14 }}>
                      <button onClick={() => { const p = Math.max(1, expPage-1); setExpPage(p); loadExp(selectedFY, p); }}
                        disabled={expPage===1}
                        style={{ background: expPage===1?'#f3f4f6':M, color: expPage===1?'#9ca3af':G, border:'none', borderRadius:8, padding:'7px 14px', fontSize:12, fontWeight:700, cursor:expPage===1?'default':'pointer' }}>← पिछला</button>
                      <span style={{ fontSize:12, color:'#6b7280', alignSelf:'center' }}>{expPage} / {Math.ceil(expData.total/expData.limit)}</span>
                      <button onClick={() => { const p=expPage+1; setExpPage(p); loadExp(selectedFY, p); }}
                        disabled={expPage>=Math.ceil(expData.total/expData.limit)}
                        style={{ background: expPage>=Math.ceil(expData.total/expData.limit)?'#f3f4f6':M, color: expPage>=Math.ceil(expData.total/expData.limit)?'#9ca3af':G, border:'none', borderRadius:8, padding:'7px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>अगला →</button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign:'center', padding:32, color:'#9ca3af', fontSize:12 }}>
                  {selectedFY ? `FY ${selectedFY} में कोई कार्य नहीं मिला` : 'कोई विवरण उपलब्ध नहीं'}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af', fontSize: 13 }}>लोड नहीं हो सका</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Combined Contribution Tab — ABRSVS + OTHER ────────────────────────────────
export function CombinedContributionTab({ profile }) {
  const [subTab, setSubTab] = useState('abrsvs');
  const tabs = [
    { key: 'abrsvs', label: 'ABRSVS',       badge: M,        desc: 'Shiksha Nidhi — SN-ABRSVS' },
    { key: 'other',  label: 'OTHER',         badge: '#7a0000', desc: 'समाजिक योगदान / Yogdan' },
  ];
  return (
    <div>
      {/* Sub-tab switcher */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, background: '#f3f4f6', borderRadius: 12, padding: 4 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: subTab === t.key ? '#fff' : 'transparent',
            boxShadow: subTab === t.key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
            transition: 'all 0.15s',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: subTab === t.key ? '#111827' : '#6b7280' }}>{t.label}</span>
              <span style={{ background: t.badge, color: '#FFD700', fontSize: 8, fontWeight: 900, borderRadius: 3, padding: '1px 5px', letterSpacing: 0.4 }}>
                {t.key === 'abrsvs' ? 'SN-ABRSVS' : 'OTHER'}
              </span>
            </div>
            {subTab === t.key && (
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{t.desc}</div>
            )}
          </button>
        ))}
      </div>
      {subTab === 'abrsvs' && <CommunityTab profile={profile} />}
      {subTab === 'other'  && <ContributionsTab profile={profile} />}
    </div>
  );
}

